/* eslint-disable complexity */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '@futrli/localisation';
import { ContactDetails } from '@futrli-sdk/predict';

import { CUSTOMER_SUPPLIER_KIND } from '../const';

import * as styles from './risk-trend.scss';

const HIGH_RISK_TREND = 10;
const MIN_MEDIUM_RISK_TREND = 1;
const MAX_MEDIUM_RISK_TREND = 9;

export const RiskTrend = ({ activeData }: { activeData?: ContactDetails }) => {
  if (!activeData) {
    return null;
  }

  const {
    average_days_to_pay_last_3_invoices,
    average_days_to_pay_last_10_invoices,
    average_days_to_pay_this_year,
    average_days_to_pay_last_year
  } = activeData;

  const currentRiskTrend = average_days_to_pay_last_3_invoices - average_days_to_pay_last_10_invoices;
  const annualRiskTrend = average_days_to_pay_this_year - average_days_to_pay_last_year;

  const increasingAnnualRiskTrend = annualRiskTrend >= MIN_MEDIUM_RISK_TREND;
  const sameAnnualRiskTrend = annualRiskTrend === 0;

  return (
    <>
      <div style={{ marginTop: 32, marginBottom: 8, display: 'flex' }}>
        <div className={classNames(styles.labelFont, styles.labelGrey)} style={{ marginRight: 16 }}>
          <FormattedMessage id="predict.current_risk_trend" defaultMessage="Current risk trend" />
        </div>
        <div
          className={classNames(styles.dot, {
            [styles.highRisk]: currentRiskTrend >= HIGH_RISK_TREND,
            [styles.mediumRisk]: currentRiskTrend >= MIN_MEDIUM_RISK_TREND && currentRiskTrend <= MAX_MEDIUM_RISK_TREND,
            [styles.noRisk]: currentRiskTrend <= 0
          })}
        />
      </div>
      {currentRiskTrend >= MIN_MEDIUM_RISK_TREND ? (
        <div className={styles.basicFont} style={{ fontWeight: 400, marginBottom: 32 }}>
          {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
            <FormattedMessage
              id="predict.current_risk_trend_customer_explained"
              defaultMessage="It’s taken {currentRiskTrend, number} {currentRiskTrend, plural, one {day} other {days}} longer for your last 3 invoices to get paid than the average over the last 10 invoices."
              values={{ currentRiskTrend }}
            />
          ) : (
            <FormattedMessage
              id="predict.current_risk_trend_supplier_explained"
              defaultMessage="It’s taken {currentRiskTrend, number} {currentRiskTrend, plural, one {day} other {days}} longer for you to pay your last 3 bills than the average over the last 10 bills."
              values={{ currentRiskTrend }}
            />
          )}
        </div>
      ) : currentRiskTrend === 0 ? (
        <div className={styles.basicFont} style={{ fontWeight: 400, marginBottom: 32 }}>
          {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
            <FormattedMessage
              id="predict.current_risk_trend_no_change_customer"
              defaultMessage="There has been no change in the number of days taken to get paid compared to the average over the last 10 invoices."
            />
          ) : (
            <FormattedMessage
              id="predict.current_risk_trend_no_change_supplier"
              defaultMessage="There has been no change in the number of days taken for you to pay bills compared to the average over the last 10 bills."
            />
          )}
        </div>
      ) : (
        <div className={styles.basicFont} style={{ fontWeight: 400, marginBottom: 32 }}>
          {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
            <FormattedMessage
              id="predict.current_risk_trend_less_than_customer"
              defaultMessage="It’s taken {currentRiskTrend, number} {currentRiskTrend, plural, one {day} other {days}} less for your last 3 invoices to get paid than the average over the last 10 invoices."
              values={{ currentRiskTrend: Math.abs(currentRiskTrend) }}
            />
          ) : (
            <FormattedMessage
              id="predict.current_risk_trend_less_than_supplier"
              defaultMessage="It’s taken {currentRiskTrend, number} {currentRiskTrend, plural, one {day} other {days}} less for you to pay your last 3 bills than the average over the last 10 bills."
              values={{ currentRiskTrend: Math.abs(currentRiskTrend) }}
            />
          )}
        </div>
      )}
      {activeData.paid_invoices_this_year > 0 && activeData.paid_invoices_last_year > 0 && (
        <>
          <div style={{ marginBottom: 8, display: 'flex' }}>
            <div className={classNames(styles.labelFont, styles.labelGrey)} style={{ marginRight: 16 }}>
              <FormattedMessage id="predict.annual_risk_trend" defaultMessage="Annual risk trend" />
            </div>
            <div
              className={classNames(styles.dot, {
                [styles.highRisk]: annualRiskTrend >= HIGH_RISK_TREND,
                [styles.mediumRisk]:
                  annualRiskTrend >= MIN_MEDIUM_RISK_TREND && annualRiskTrend <= MAX_MEDIUM_RISK_TREND,
                [styles.noRisk]: annualRiskTrend <= 0
              })}
            />
          </div>
          <div className={styles.basicFont} style={{ fontWeight: 400 }}>
            {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
              <>
                {increasingAnnualRiskTrend ? (
                  <FormattedMessage
                    id="predict.annual_risk_customer_increasing"
                    defaultMessage="The length of time to be paid is increasing in comparison to last year."
                  />
                ) : sameAnnualRiskTrend ? (
                  <FormattedMessage
                    id="predict.annual_risk_customer_same"
                    defaultMessage="The length of time to be paid is same in comparison to last year."
                  />
                ) : (
                  <FormattedMessage
                    id="predict.annual_risk_customer_decreasing"
                    defaultMessage="The length of time to be paid is decreasing in comparison to last year."
                  />
                )}

                {annualRiskTrend > 0 && (
                  <>
                    &nbsp;
                    <FormattedMessage
                      id="predict.takes_n_days_longer"
                      defaultMessage="It now takes {annualRiskTrend, number} {annualRiskTrend, plural, one {day} other {days}} longer."
                      values={{ annualRiskTrend }}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                {increasingAnnualRiskTrend ? (
                  <FormattedMessage
                    id="predict.annual_risk_supplier_increasing"
                    defaultMessage="The length of time you are taking to pay bills is increasing in comparison to last year."
                  />
                ) : sameAnnualRiskTrend ? (
                  <FormattedMessage
                    id="predict.annual_risk_supplier_same"
                    defaultMessage="The length of time you are taking to pay bills is same in comparison to last year."
                  />
                ) : (
                  <FormattedMessage
                    id="predict.annual_risk_supplier_decreasing"
                    defaultMessage="The length of time you are taking to pay bills is decreasing in comparison to last year."
                  />
                )}
                {annualRiskTrend > 0 && (
                  <>
                    &nbsp;
                    <FormattedMessage
                      id="predict.takes_n_days_longer"
                      defaultMessage="It now takes {annualRiskTrend, number} {annualRiskTrend, plural, one {day} other {days}} longer."
                      values={{ annualRiskTrend }}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
